import React, { useEffect, useState } from 'react';
import { getContactFormLeadCategory, submitContactFormIssue } from './contactForm';

function defaultComment(vm, t) {
  const product = (vm.productName || 'Equipment').trim();
  return `${product} ${t('modal.defaultCommentSuffix')}\n\n`;
}

export function RequestServiceModal({
  open,
  onClose,
  contactConfig,
  viewModel,
  t,
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyRegNo, setCompanyRegNo] = useState('');
  const [comment, setComment] = useState(() =>
    viewModel ? defaultComment(viewModel, t) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open || !viewModel) return;
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCompanyName('');
    setCompanyRegNo('');
    setComment(defaultComment(viewModel, t));
    setError(null);
    setDone(false);
  }, [open, viewModel, t]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const reqType = (contactConfig.requestType || 'QR Service Portal').trim();
    const leadCategory = getContactFormLeadCategory();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError(t('modal.requiredError'));
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        person: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        },
        company: {
          companyName: companyName.trim() || '',
          companyRegNo: companyRegNo.trim() || '',
        },
        contacts: {
          email: email.trim(),
          phone: phone.trim() || '',
        },
        comment: comment.trim(),
        interestedIn: [],
        leadCategory,
        trackingCode: '',
        url: '',
        requestType: reqType,
        formFields: {},
      };

      const { res, data } = await submitContactFormIssue({ body });

      if (!res.ok) {
        const msg =
          (data && typeof data === 'object' && (data.error || data.detail || data.message)) ||
          `${t('app.requestFailed')} (${res.status})`;
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      setDone(true);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setDone(false);
    if (!submitting) onClose();
  };

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) handleClose();
      }}
    >
      <div
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-service-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="request-service-title">{t('modal.title')}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
            disabled={submitting}
            aria-label={t('modal.close')}
          >
            ×
          </button>
        </div>

        {done ? (
          <div className="modal-body">
            <p className="modal-success">{t('modal.success')}</p>
            <button type="button" className="btn-primary" onClick={handleClose}>
              {t('modal.close')}
            </button>
          </div>
        ) : (
          <form className="modal-body" onSubmit={handleSubmit}>
            <p className="modal-intro">
              {t('modal.intro')}
            </p>
            <div className="form-row two">
              <label className="form-field">
                <span>{t('modal.firstName')}</span>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoComplete="given-name"
                  disabled={submitting}
                />
              </label>
              <label className="form-field">
                <span>{t('modal.lastName')}</span>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  autoComplete="family-name"
                  disabled={submitting}
                />
              </label>
            </div>
            <label className="form-field">
              <span>{t('modal.email')}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={submitting}
              />
            </label>
            <label className="form-field">
              <span>{t('modal.phoneOptional')}</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                disabled={submitting}
              />
            </label>
            <label className="form-field">
              <span>{t('modal.companyOptional')}</span>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="organization"
                disabled={submitting}
              />
            </label>
            <label className="form-field">
              <span>{t('modal.companyRegNoOptional')}</span>
              <input
                type="text"
                value={companyRegNo}
                onChange={(e) => setCompanyRegNo(e.target.value)}
                autoComplete="off"
                disabled={submitting}
              />
            </label>
            <label className="form-field">
              <span>{t('modal.details')}</span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                disabled={submitting}
              />
            </label>
            {error && <p className="modal-error">{error}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClose}
                disabled={submitting}
              >
                {t('modal.cancel')}
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? t('modal.sending') : t('modal.sendRequest')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
